import React, { useRef } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { motion, useInView } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import EventNoteIcon from '@mui/icons-material/EventNote';
import PaymentsIcon from '@mui/icons-material/Payments';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AppleIcon from '@mui/icons-material/Apple';
import { P, G, S } from '../theme/palette';
import { useTenantConfig } from '../hooks/useTenant';


const float1 = keyframes`
  0%,100% { transform: translateY(0); }
  50%     { transform: translateY(-14px); }
`;
const float2 = keyframes`
  0%,100% { transform: translateY(0); }
  50%     { transform: translateY(-10px); }
`;
const pulseGlow = keyframes`
  0%,100% { opacity: 0.4; box-shadow: 0 0 20px rgba(37,99,235,0.15); }
  50%     { opacity: 0.7; box-shadow: 0 0 40px rgba(37,99,235,0.25); }
`;
const phonePulse = keyframes`
  0%,100% { box-shadow: 0 40px 100px rgba(37,99,235,0.2), 0 0 0 0px rgba(37,99,235,0.08); }
  50%     { box-shadow: 0 40px 100px rgba(37,99,235,0.3), 0 0 0 12px rgba(37,99,235,0.04); }
`;


const D = {
  bg: G.bgDark,
  text: '#f1f5f9',
  textMuted: '#94a3b8',
  textDim: '#64748b',
  cardBg: 'rgba(255,255,255,0.04)',
  cardBorder: 'rgba(255,255,255,0.08)',
};

/* =======================================
   STYLED
   ======================================= */
const DarkBadge = styled(motion.div)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 20px',
  borderRadius: 50,
  background: 'rgba(255,255,255,0.06)',
  backdropFilter: 'blur(12px)',
  border: '1px solid rgba(255,255,255,0.1)',
  marginBottom: 24,
});

const GradientBtn = styled(Button)({
  fontWeight: 700,
  fontSize: '0.95rem',
  textTransform: 'none',
  borderRadius: 14,
  padding: '14px 32px',
  color: '#fff',
  background: G.primary,
  backgroundSize: '200% auto',
  boxShadow: S.glow,
  transition: 'all 0.4s cubic-bezier(0.4,0,0.2,1)',
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: `0 16px 48px ${G.glowBlue}0.3)`,
    backgroundPosition: 'right center',
  },
});

const StoreBtn = styled(Button)({
  fontWeight: 600,
  fontSize: '0.88rem',
  textTransform: 'none',
  borderRadius: 14,
  padding: '12px 24px',
  color: '#fff',
  border: '1px solid rgba(255,255,255,0.15)',
  background: 'rgba(255,255,255,0.06)',
  backdropFilter: 'blur(8px)',
  transition: 'all 0.35s ease',
  gap: 8,
  '&:hover': {
    background: 'rgba(255,255,255,0.12)',
    borderColor: 'rgba(255,255,255,0.25)',
    transform: 'translateY(-2px)',
  },
});

const APP_FEATURES = [
  {
    icon: NotificationsActiveIcon,
    title: 'Anlık Bildirimler',
    desc: 'Önemli gelişmeler için push bildirimleri. Hiçbir şeyi kaçırmayın.',
    gradient: `linear-gradient(135deg, ${P.blue[600]}, ${P.blue[400]})`,
  },
  {
    icon: EventNoteIcon,
    title: 'Etkinlik Takibi',
    desc: 'Etkinlikleri mobilden takip edin, katılım onaylayın.',
    gradient: `linear-gradient(135deg, ${P.teal[500]}, ${P.teal[300]})`,
  },
  {
    icon: PaymentsIcon,
    title: 'Ödeme Yönetimi',
    desc: 'Aidat ve ödemeleri tek dokunuşla yönetin ve takip edin.',
    gradient: `linear-gradient(135deg, #0891b2, ${P.blue[500]})`,
  },
];

const containerV = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.5, staggerChildren: 0.12 } },
};
const itemV = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};
const leftV = {
  hidden: { opacity: 0, x: -50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};
const rightV = {
  hidden: { opacity: 0, x: 50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};

const MobileAppSection = () => {
  const navigate = useNavigate();
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-60px' });
  const { tenantName } = useTenantConfig();

  return (
    <Box
      ref={sectionRef}
      sx={{
        position: 'relative',
        py: { xs: 10, md: 14 },
        background: D.bg,
        overflow: 'hidden',
      }}
    >
      {/* ── Background effects ── */}
      <Box sx={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        background: `radial-gradient(ellipse at 20% 80%, ${G.glowBlue}0.06) 0%, transparent 50%),
                     radial-gradient(ellipse at 80% 20%, ${G.glowTeal}0.05) 0%, transparent 50%)`,
      }} />
      <Box sx={{
        position: 'absolute', width: 500, height: 500, top: -200, right: -200,
        background: `radial-gradient(circle, ${G.glowBlue}0.08) 0%, transparent 70%)`,
        filter: 'blur(100px)', pointerEvents: 'none',
        animation: `${float1} 20s ease-in-out infinite`,
      }} />
      <Box sx={{
        position: 'absolute', width: 400, height: 400, bottom: -150, left: -150,
        background: `radial-gradient(circle, ${G.glowTeal}0.06) 0%, transparent 70%)`,
        filter: 'blur(80px)', pointerEvents: 'none',
        animation: `${float2} 18s ease-in-out 3s infinite`,
      }} />
      {/* Dot grid */}
      <Box sx={{
        position: 'absolute', inset: 0, opacity: 0.08,
        backgroundImage: 'radial-gradient(rgba(255,255,255,0.3) 0.6px, transparent 0.6px)',
        backgroundSize: '30px 30px', pointerEvents: 'none',
      }} />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <motion.div
          variants={containerV}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
        >
          <Grid container spacing={{ xs: 6, md: 10 }} alignItems="center">

            {/* ════════════════════════
                LEFT — Text Content
                ════════════════════════ */}
            <Grid item xs={12} md={6}>
              <motion.div variants={leftV}>
                {/* Badge */}
                <DarkBadge
                  initial={{ opacity: 0, y: -10 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.15, duration: 0.5 }}
                >
                  <SmartphoneIcon sx={{ fontSize: 15, color: P.teal[400] }} />
                  <Typography sx={{
                    fontSize: '0.76rem', fontWeight: 700, letterSpacing: 0.8,
                    background: G.primarySoft,
                    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                  }}>
                    MOBİL UYGULAMA
                  </Typography>
                </DarkBadge>

                {/* Title */}
                <Typography variant="h2" sx={{
                  fontSize: { xs: '2.2rem', sm: '2.8rem', md: '3.2rem' },
                  fontWeight: 900, color: '#fff',
                  letterSpacing: '-0.03em', lineHeight: 1.1, mb: 2.5,
                }}>
                  {tenantName}{' '}
                  <Box component="span" sx={{
                    background: G.primarySoft,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}>
                    Mobil
                  </Box>
                </Typography>

                <Typography sx={{
                  color: D.textMuted, lineHeight: 1.8, mb: 4,
                  fontSize: { xs: '0.95rem', md: '1.1rem' },
                  maxWidth: 480,
                }}>
                  Dernek yönetimini avucunuzun içinde taşıyın.
                  iOS ve Android uygulamalarıyla her yerden erişim.
                </Typography>

                {/* Gradient divider */}
                <Box sx={{
                  width: 60, height: 3, mb: 4,
                  background: G.primary, borderRadius: 2,
                }} />

                {/* Feature cards */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 5 }}>
                  {APP_FEATURES.map((feat, idx) => (
                    <motion.div key={idx} variants={itemV}>
                      <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 2,
                        p: 2, borderRadius: '16px',
                        background: D.cardBg,
                        border: `1px solid ${D.cardBorder}`,
                        transition: 'all 0.4s cubic-bezier(0.4,0,0.2,1)',
                        '&:hover': {
                          background: 'rgba(255,255,255,0.08)',
                          borderColor: 'rgba(255,255,255,0.16)',
                          transform: 'translateX(6px)',
                          '& .feat-icon': {
                            transform: 'scale(1.1) rotate(-3deg)',
                          },
                        },
                      }}>
                        {/* Icon */}
                        <Box className="feat-icon" sx={{
                          width: 48, height: 48, borderRadius: 14,
                          background: feat.gradient,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          flexShrink: 0,
                          boxShadow: `0 6px 20px ${G.glowBlue}0.2)`,
                          transition: 'all 0.4s cubic-bezier(0.4,0,0.2,1)',
                        }}>
                          <feat.icon sx={{ fontSize: 22, color: '#fff' }} />
                        </Box>
                        {/* Text */}
                        <Box>
                          <Typography sx={{
                            fontWeight: 700, fontSize: '0.95rem',
                            color: '#fff', mb: 0.3, lineHeight: 1.3,
                          }}>
                            {feat.title}
                          </Typography>
                          <Typography sx={{
                            fontSize: '0.82rem', color: D.textMuted,
                            lineHeight: 1.5,
                          }}>
                            {feat.desc}
                          </Typography>
                        </Box>
                      </Box>
                    </motion.div>
                  ))}
                </Box>

                {/* Store Buttons */}
                <motion.div variants={itemV}>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <GradientBtn
                      startIcon={<AppleIcon />}
                      endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
                      onClick={() => navigate('/basvuru')}
                    >
                      App Store
                    </GradientBtn>
                    <StoreBtn
                      startIcon={
                        <Box component="img" src="https://upload.wikimedia.org/wikipedia/commons/d/d0/Google_Play_Arrow_logo.svg" alt="Play" sx={{ width: 18, height: 18, filter: 'brightness(10)' }} />
                      }
                      onClick={() => navigate('/basvuru')}
                    >
                      Google Play
                    </StoreBtn>
                  </Box>
                </motion.div>
              </motion.div>
            </Grid>

            {/* ════════════════════════
                RIGHT — Phone Mockup
                ════════════════════════ */}
            <Grid item xs={12} md={6}>
              <motion.div variants={rightV} style={{ display: 'flex', justifyContent: 'center' }}>
                <Box sx={{ position: 'relative' }}>

                  {/* Glow behind phone */}
                  <Box sx={{
                    position: 'absolute',
                    top: '50%', left: '50%',
                    transform: 'translate(-50%,-50%)',
                    width: '120%', height: '120%',
                    background: `radial-gradient(circle, ${G.glowBlue}0.12) 0%, ${G.glowTeal}0.06) 40%, transparent 70%)`,
                    filter: 'blur(60px)',
                    pointerEvents: 'none',
                    animation: `${pulseGlow} 4s ease-in-out infinite`,
                  }} />

                  {/* Floating badge — top right */}
                  <Box sx={{
                    position: 'absolute', top: 20, right: -40, zIndex: 10,
                    background: 'rgba(255,255,255,0.1)',
                    backdropFilter: 'blur(16px)',
                    borderRadius: 14, px: 2, py: 1,
                    border: '1px solid rgba(255,255,255,0.12)',
                    display: { xs: 'none', md: 'flex' },
                    alignItems: 'center', gap: 1,
                    animation: `${float1} 6s ease-in-out infinite`,
                  }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 8px rgba(34,197,94,0.5)' }} />
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#fff' }}>Canlı</Typography>
                  </Box>

                  {/* Floating badge — bottom left */}
                  <Box sx={{
                    position: 'absolute', bottom: 40, left: -30, zIndex: 10,
                    background: 'rgba(255,255,255,0.1)',
                    backdropFilter: 'blur(16px)',
                    borderRadius: 14, px: 2, py: 1,
                    border: '1px solid rgba(255,255,255,0.12)',
                    display: { xs: 'none', md: 'flex' },
                    alignItems: 'center', gap: 1,
                    animation: `${float2} 7s ease-in-out 1s infinite`,
                  }}>
                    <TouchAppIcon sx={{ fontSize: 16, color: P.teal[400] }} />
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#fff' }}>Tek Dokunuş</Typography>
                  </Box>

                  {/* ── PHONE FRAME ── */}
                  <Box sx={{
                    position: 'relative',
                    width: { xs: 260, md: 290 },
                    height: { xs: 530, md: 590 },
                    borderRadius: '40px',
                    background: 'linear-gradient(160deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.04) 100%)',
                    border: '1px solid rgba(255,255,255,0.15)',
                    p: '8px',
                    animation: `${phonePulse} 4s ease-in-out infinite`,
                  }}>
                    {/* Inner screen */}
                    <Box sx={{
                      width: '100%', height: '100%',
                      borderRadius: '32px',
                      background: 'linear-gradient(160deg, #1e293b 0%, #0f172a 100%)',
                      overflow: 'hidden',
                      position: 'relative',
                    }}>
                      {/* Notch */}
                      <Box sx={{
                        position: 'absolute', top: 0, left: '50%',
                        transform: 'translateX(-50%)',
                        width: 130, height: 28,
                        background: '#0f172a',
                        borderRadius: '0 0 18px 18px',
                        zIndex: 5,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Box sx={{ width: 50, height: 5, borderRadius: 3, background: 'rgba(255,255,255,0.1)' }} />
                      </Box>

                      {/* Screen content */}
                      <Box sx={{ p: 2.5, pt: 5, height: '100%', display: 'flex', flexDirection: 'column' }}>
                        {/* Status bar */}
                        <Box sx={{
                          display: 'flex', justifyContent: 'space-between',
                          fontSize: '0.68rem', color: 'rgba(255,255,255,0.5)',
                          mb: 2.5, fontWeight: 600,
                        }}>
                          <span>9:41</span>
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                            <Box sx={{ width: 14, height: 8, borderRadius: 1, border: '1px solid rgba(255,255,255,0.4)', position: 'relative' }}>
                              <Box sx={{ position: 'absolute', top: 1, left: 1, right: 2, bottom: 1, background: '#22c55e', borderRadius: 0.5 }} />
                            </Box>
                          </Box>
                        </Box>

                        {/* App header */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                          <Box sx={{
                            width: 36, height: 36, borderRadius: 10,
                            background: G.primary,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            <AutoAwesomeIcon sx={{ fontSize: 18, color: '#fff' }} />
                          </Box>
                          <Box>
                            <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: '#fff', lineHeight: 1.2 }}>
                              {tenantName}
                            </Typography>
                            <Typography sx={{ fontSize: '0.6rem', color: D.textDim }}>
                              Hoş geldiniz
                            </Typography>
                          </Box>
                        </Box>

                        {/* Dashboard cards */}
                        <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5 }}>
                          {[
                            { label: 'Toplam Üye', val: '1.247', gradient: `linear-gradient(135deg, ${P.blue[600]}, ${P.blue[400]})` },
                            { label: 'Aktif Etkinlik', val: '12', gradient: `linear-gradient(135deg, ${P.teal[500]}, ${P.teal[300]})` },
                          ].map((card, i) => (
                            <Box key={i} sx={{
                              flex: 1, p: 1.5, borderRadius: '14px',
                              background: card.gradient,
                              position: 'relative', overflow: 'hidden',
                            }}>
                              <Box sx={{
                                position: 'absolute', top: -10, right: -10,
                                width: 40, height: 40, borderRadius: '50%',
                                background: 'rgba(255,255,255,0.15)',
                              }} />
                              <Typography sx={{ fontSize: '0.58rem', color: 'rgba(255,255,255,0.8)', fontWeight: 600 }}>
                                {card.label}
                              </Typography>
                              <Typography sx={{ fontWeight: 900, fontSize: '1.3rem', color: '#fff', lineHeight: 1.3 }}>
                                {card.val}
                              </Typography>
                            </Box>
                          ))}
                        </Box>

                        {/* Gelir card */}
                        <Box sx={{
                          p: 1.5, borderRadius: '14px', mb: 2.5,
                          background: 'rgba(255,255,255,0.06)',
                          border: '1px solid rgba(255,255,255,0.08)',
                        }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography sx={{ fontSize: '0.65rem', color: D.textMuted, fontWeight: 600 }}>
                              Aylık Gelir
                            </Typography>
                            <Typography sx={{
                              fontSize: '0.55rem', fontWeight: 700, px: 0.8, py: 0.2,
                              borderRadius: 4, background: 'rgba(34,197,94,0.15)', color: '#22c55e',
                            }}>
                              +%12
                            </Typography>
                          </Box>
                          <Typography sx={{ fontWeight: 900, fontSize: '1.1rem', color: '#fff' }}>
                            24.580 TL
                          </Typography>
                          {/* Mini chart */}
                          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '3px', mt: 1, height: 28 }}>
                            {[35, 50, 40, 65, 55, 80, 70, 90, 75, 95, 85, 100].map((h, i) => (
                              <Box key={i} sx={{
                                flex: 1, height: `${h}%`, borderRadius: 2,
                                background: i >= 10 ? G.primary : `rgba(59,130,246,${0.15 + (h / 250)})`,
                              }} />
                            ))}
                          </Box>
                        </Box>

                        {/* Quick actions */}
                        <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: D.textMuted, mb: 1, letterSpacing: 0.3 }}>
                          Hızlı İşlemler
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
                          {[
                            { icon: SmartphoneIcon, label: 'Üye Ekle', color: P.blue[500] },
                            { icon: EventNoteIcon, label: 'Etkinlik', color: P.teal[500] },
                            { icon: PaymentsIcon, label: 'Ödeme', color: '#0891b2' },
                          ].map((act, i) => (
                            <Box key={i} sx={{
                              p: 1.2, borderRadius: '12px',
                              background: 'rgba(255,255,255,0.05)',
                              border: '1px solid rgba(255,255,255,0.06)',
                              textAlign: 'center',
                            }}>
                              <act.icon sx={{ fontSize: 18, color: act.color, mb: 0.3 }} />
                              <Typography sx={{ fontSize: '0.55rem', color: D.textMuted, fontWeight: 600 }}>
                                {act.label}
                              </Typography>
                            </Box>
                          ))}
                        </Box>

                        {/* Bottom nav */}
                        <Box sx={{ mt: 'auto', pt: 1.5 }}>
                          <Box sx={{
                            display: 'flex', justifyContent: 'space-around',
                            py: 1, borderTop: '1px solid rgba(255,255,255,0.06)',
                          }}>
                            {['Ana Sayfa', 'Üyeler', 'Profil'].map((tab, i) => (
                              <Typography key={i} sx={{
                                fontSize: '0.55rem', fontWeight: 600,
                                color: i === 0 ? P.blue[400] : D.textDim,
                              }}>
                                {tab}
                              </Typography>
                            ))}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  {/* Home indicator */}
                  <Box sx={{
                    position: 'absolute', bottom: 12, left: '50%',
                    transform: 'translateX(-50%)',
                    width: 100, height: 4, borderRadius: 2,
                    background: 'rgba(255,255,255,0.15)',
                  }} />
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </motion.div>
      </Container>
    </Box>
  );
};

export default MobileAppSection;
